import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "sonner";
import {
  ArrowLeft,
  ArrowRight,
  Check,
  Loader2,
  Trophy,
  Upload,
  X,
} from "lucide-react";
import axios from "axios";
import WhatsAppIcon from "@/components/WhatsAppIcon";
import { buildWhatsAppLink } from "@/components/FloatingWhatsAppButton";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const STEPS = [
  "Personal",
  "Competition",
  "Training",
  "Nutrition",
  "Lifestyle",
  "Health",
  "Progress",
  "Final",
  "Package",
];

const initial = {
  // Personal
  full_name: "",
  email: "",
  whatsapp: "",
  country: "",
  age: "",
  height: "",
  weight: "",
  // Competition
  currently_competing: "",
  category: "",
  federation: "",
  competition_experience: "",
  next_competition_date: "",
  goal: "",
  // Training
  years_training: "",
  training_days_per_week: "",
  has_coach: "",
  cardio_frequency: "",
  // Nutrition
  follows_meal_plan: "",
  current_diet: "",
  food_allergies: "",
  foods_disliked: "",
  digestive_issues: [],
  // Lifestyle
  occupation: "",
  daily_activity: "",
  sleep_hours: "",
  stress_level: "",
  // Health
  current_medications: "",
  current_supplements: "",
  previous_injuries: "",
  medical_conditions: "",
  // Progress
  front_photo: "",
  side_photo: "",
  back_photo: "",
  posing_video: "",
  // Final
  biggest_goal: "",
  whats_holding_back: "",
  // Package
  interested_service: "",
  preferred_contact: "",
};

// Compress image to max 1200px width jpeg 0.8 quality
const compressImage = (file) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onerror = reject;
    reader.onload = (e) => {
      const img = new Image();
      img.onload = () => {
        const maxW = 1200;
        const scale = Math.min(1, maxW / img.width);
        const canvas = document.createElement("canvas");
        canvas.width = Math.round(img.width * scale);
        canvas.height = Math.round(img.height * scale);
        const ctx = canvas.getContext("2d");
        ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
        resolve(canvas.toDataURL("image/jpeg", 0.8));
      };
      img.onerror = reject;
      img.src = e.target.result;
    };
    reader.readAsDataURL(file);
  });

const CoachingApplicationPage = () => {
  const [step, setStep] = useState(0);
  const [data, setData] = useState(initial);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const set = (k, v) => setData((d) => ({ ...d, [k]: v }));
  const toggleDigestive = (opt) =>
    setData((d) => {
      const cur = d.digestive_issues || [];
      const on = cur.includes(opt);
      // "None" is mutually exclusive
      if (opt === "None") return { ...d, digestive_issues: on ? [] : ["None"] };
      const next = on ? cur.filter((x) => x !== opt) : [...cur.filter((x) => x !== "None"), opt];
      return { ...d, digestive_issues: next };
    });

  const handlePhoto = async (key, file) => {
    if (!file) return;
    if (file.size > 8 * 1024 * 1024) {
      toast.error("Photo too large — please use a file under 8MB");
      return;
    }
    try {
      const b64 = await compressImage(file);
      set(key, b64);
    } catch {
      toast.error("Could not read that photo — try another one");
    }
  };

  // Per-step validation (only Personal + Package are required)
  const stepValid = () => {
    switch (step) {
      case 0:
        return (
          data.full_name.trim().length >= 2 &&
          data.email.trim().length >= 5 &&
          data.whatsapp.trim().length >= 5
        );
      case 8:
        return !!data.interested_service && !!data.preferred_contact;
      default:
        return true;
    }
  };

  const next = () => {
    if (!stepValid()) {
      toast.error("Please complete the required fields to continue");
      return;
    }
    setStep((s) => Math.min(STEPS.length - 1, s + 1));
    window.scrollTo({ top: 0, behavior: "smooth" });
  };
  const back = () => {
    setStep((s) => Math.max(0, s - 1));
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const handleSubmit = async () => {
    if (!stepValid()) {
      toast.error("Please choose an interested service and contact preference");
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/coaching-applications`, data);
      setSubmitted(true);
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (err) {
      const detail = err.response?.data?.detail;
      const msg =
        typeof detail === "string"
          ? detail
          : Array.isArray(detail)
          ? detail.map((d) => d.msg).join(", ")
          : "Submission failed. Please try again.";
      toast.error(msg);
    } finally {
      setSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <div className="min-h-screen bg-[#0A0A0A] pt-32 pb-24 px-6" data-testid="coaching-submitted">
        <div className="max-w-2xl mx-auto text-center">
          <div className="inline-flex items-center justify-center w-20 h-20 border-2 border-[#D4AF37] rounded-full mb-8">
            <Check size={32} className="text-[#D4AF37]" />
          </div>
          <p className="text-[#D4AF37] text-xs tracking-[0.3em] uppercase mb-4">
            Application Received
          </p>
          <h1
            className="font-display text-4xl sm:text-5xl lg:text-6xl text-white mb-6 leading-[0.95]"
            style={{ fontFamily: "'Bebas Neue', sans-serif" }}
          >
            THANK YOU,{" "}
            <span className="text-[#D4AF37]">
              {data.full_name.split(" ")[0] || "Athlete"}
            </span>
          </h1>
          <p className="text-white/70 text-base sm:text-lg leading-relaxed mb-10">
            Chris and his team will review your submission and reach out on your
            preferred channel within{" "}
            <span className="text-white font-semibold">24–48 hours</span>.
          </p>
          <a
            href={buildWhatsAppLink()}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-3 bg-[#25D366] hover:bg-[#1FB855] text-white px-9 py-4 text-xs tracking-[0.25em] uppercase font-bold transition-all shadow-[0_15px_45px_-10px_rgba(37,211,102,0.55)]"
            data-testid="submitted-whatsapp-btn"
          >
            <WhatsAppIcon size={20} />
            Message Us on WhatsApp
          </a>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0A0A0A] pt-24 sm:pt-28 pb-20" data-testid="coaching-application-page">
      <div className="max-w-3xl mx-auto px-5 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-8 sm:mb-10">
          <div className="inline-flex items-center gap-2 border border-[#D4AF37]/50 bg-black/40 px-4 py-1.5 mb-5">
            <Trophy className="w-3 h-3 text-[#D4AF37]" />
            <span className="text-[#D4AF37] text-[10px] sm:text-xs tracking-[0.4em] uppercase font-semibold">
              Coaching Application
            </span>
          </div>
          <h1
            className="font-display text-[2rem] sm:text-4xl lg:text-5xl text-white leading-[0.95] mb-3 tracking-tight"
            style={{ fontFamily: "'Bebas Neue', sans-serif" }}
          >
            NUTRITION, TRAINING & <br className="hidden sm:block" />
            <span className="text-[#D4AF37]">CONTEST PREP</span> APPLICATION
          </h1>
          <p className="text-white/55 text-sm max-w-md mx-auto leading-relaxed">
            Complete evaluation — from off-season to contest day. Answer honestly for
            the best fit.
          </p>
        </div>

        {/* Progress bar */}
        <div className="mb-6 sm:mb-8" data-testid="progress-bar">
          <div className="flex justify-between text-[10px] tracking-[0.2em] uppercase text-white/40 mb-2 gap-1 overflow-x-auto">
            {STEPS.map((s, i) => (
              <span
                key={s}
                className={`flex-1 text-center whitespace-nowrap ${
                  i === step ? "text-[#D4AF37]" : i < step ? "text-white/70" : ""
                }`}
              >
                {s}
              </span>
            ))}
          </div>
          <div className="h-[3px] bg-white/10 relative overflow-hidden">
            <div
              className="absolute inset-y-0 left-0 bg-[#D4AF37] transition-all duration-500"
              style={{ width: `${((step + 1) / STEPS.length) * 100}%` }}
            />
          </div>
        </div>

        {/* Form card */}
        <div className="border border-[#1A1A1A] bg-black/60 backdrop-blur-sm p-5 sm:p-8">
          <StepHeading index={step + 1} total={STEPS.length} label={STEPS[step]} />

          {/* ============ STEPS ============ */}
          {step === 0 && (
            <StepBox testid="step-personal">
              <F label="Full Name *">
                <Input data-testid="fld-full-name" className="premium-input rounded" value={data.full_name} onChange={(e) => set("full_name", e.target.value)} placeholder="First Last" />
              </F>
              <F label="Email Address *">
                <Input data-testid="fld-email" type="email" autoComplete="email" className="premium-input rounded" value={data.email} onChange={(e) => set("email", e.target.value)} placeholder="you@email.com" />
              </F>
              <F label="WhatsApp Number *" hint="Include country code.">
                <Input data-testid="fld-whatsapp" inputMode="tel" className="premium-input rounded" value={data.whatsapp} onChange={(e) => set("whatsapp", e.target.value)} placeholder="+1 555 000 0000" />
              </F>
              <TwoCol>
                <F label="Country">
                  <Input data-testid="fld-country" className="premium-input rounded" value={data.country} onChange={(e) => set("country", e.target.value)} placeholder="United States" />
                </F>
                <F label="Age">
                  <Input data-testid="fld-age" inputMode="numeric" className="premium-input rounded" value={data.age} onChange={(e) => set("age", e.target.value)} placeholder="28" />
                </F>
              </TwoCol>
              <TwoCol>
                <F label="Height">
                  <Input data-testid="fld-height" className="premium-input rounded" value={data.height} onChange={(e) => set("height", e.target.value)} placeholder="5'10 / 178 cm" />
                </F>
                <F label="Current Weight">
                  <Input data-testid="fld-weight" className="premium-input rounded" value={data.weight} onChange={(e) => set("weight", e.target.value)} placeholder="200 lbs / 91 kg" />
                </F>
              </TwoCol>
            </StepBox>
          )}

          {step === 1 && (
            <StepBox testid="step-competition">
              <F label="Are you currently competing?">
                <PillRadio
                  testid="fld-currently-competing"
                  value={data.currently_competing}
                  onChange={(v) => set("currently_competing", v)}
                  options={["Yes", "No"]}
                />
              </F>
              <F label="Category">
                <PillRadio
                  testid="fld-category"
                  value={data.category}
                  onChange={(v) => set("category", v)}
                  cols={2}
                  options={[
                    "Men's Bodybuilding",
                    "Classic Physique",
                    "Men's Physique",
                    "Wellness",
                    "Bikini",
                    "Figure",
                    "Women's Physique",
                    "Other",
                  ]}
                />
              </F>
              <F label="Federation">
                <PillRadio
                  testid="fld-federation"
                  value={data.federation}
                  onChange={(v) => set("federation", v)}
                  cols={2}
                  options={["NPC", "IFBB Pro League", "IFBB Elite", "Other"]}
                />
              </F>
              <F label="Competition Experience">
                <PillRadio
                  testid="fld-competition-experience"
                  value={data.competition_experience}
                  onChange={(v) => set("competition_experience", v)}
                  cols={2}
                  options={["First Show", "1–3 Shows", "4–10 Shows", "10+"]}
                />
              </F>
              <F label="Next Competition Date" hint="Optional">
                <Input data-testid="fld-next-competition-date" className="premium-input rounded" value={data.next_competition_date} onChange={(e) => set("next_competition_date", e.target.value)} placeholder="e.g. NPC USA · July 2026" />
              </F>
              <F label="Goal">
                <PillRadio
                  testid="fld-goal"
                  value={data.goal}
                  onChange={(v) => set("goal", v)}
                  cols={2}
                  options={[
                    "Fat Loss",
                    "Muscle Gain",
                    "Body Recomposition",
                    "Contest Prep",
                    "Off Season",
                    "Lifestyle",
                  ]}
                />
              </F>
            </StepBox>
          )}

          {step === 2 && (
            <StepBox testid="step-training">
              <TwoCol>
                <F label="Years of training">
                  <Input data-testid="fld-years-training" className="premium-input rounded" value={data.years_training} onChange={(e) => set("years_training", e.target.value)} placeholder="e.g. 8 years" />
                </F>
                <F label="Days you train per week">
                  <Input data-testid="fld-training-days" className="premium-input rounded" value={data.training_days_per_week} onChange={(e) => set("training_days_per_week", e.target.value)} placeholder="5" />
                </F>
              </TwoCol>
              <F label="Do you currently have a coach?">
                <PillRadio testid="fld-has-coach" value={data.has_coach} onChange={(v) => set("has_coach", v)} options={["Yes", "No"]} />
              </F>
              <F label="Current cardio">
                <PillRadio
                  testid="fld-cardio-frequency"
                  value={data.cardio_frequency}
                  onChange={(v) => set("cardio_frequency", v)}
                  cols={2}
                  options={["None", "1–3 sessions", "4–6 sessions", "Daily"]}
                />
              </F>
            </StepBox>
          )}

          {step === 3 && (
            <StepBox testid="step-nutrition">
              <F label="Do you currently follow a meal plan?">
                <PillRadio testid="fld-follows-meal-plan" value={data.follows_meal_plan} onChange={(v) => set("follows_meal_plan", v)} options={["Yes", "No"]} />
              </F>
              <F label="Meals per day" hint="Write your current diet — meals and rough macros/foods.">
                <Textarea data-testid="fld-current-diet" className="premium-textarea min-h-[110px] rounded" value={data.current_diet} onChange={(e) => set("current_diet", e.target.value)} placeholder="e.g. 5 meals — chicken, rice, egg whites…" />
              </F>
              <F label="Any food allergies or intolerances?">
                <Textarea data-testid="fld-food-allergies" className="premium-textarea rounded" value={data.food_allergies} onChange={(e) => set("food_allergies", e.target.value)} placeholder="e.g. lactose, shellfish…" />
              </F>
              <F label="Foods you dislike">
                <Textarea data-testid="fld-foods-disliked" className="premium-textarea rounded" value={data.foods_disliked} onChange={(e) => set("foods_disliked", e.target.value)} placeholder="e.g. tuna, broccoli…" />
              </F>
              <F label="Digestive issues?">
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-2" data-testid="fld-digestive-issues">
                  {["Bloating", "Constipation", "Acid reflux", "IBS", "None"].map((opt) => {
                    const on = (data.digestive_issues || []).includes(opt);
                    return (
                      <label
                        key={opt}
                        onClick={() => toggleDigestive(opt)}
                        className={`flex items-center gap-2 py-2 px-3 border cursor-pointer transition-all text-sm ${
                          on
                            ? "border-[#D4AF37] bg-[#D4AF37]/[0.08] text-[#D4AF37]"
                            : "border-white/15 text-white/70 hover:border-white/40"
                        }`}
                        data-testid={`fld-digestive-${opt.toLowerCase().replace(/\s+/g, "-")}`}
                      >
                        <Checkbox checked={on} onCheckedChange={() => toggleDigestive(opt)} className="pointer-events-none" />
                        {opt}
                      </label>
                    );
                  })}
                </div>
              </F>
            </StepBox>
          )}

          {step === 4 && (
            <StepBox testid="step-lifestyle">
              <F label="Occupation">
                <Input data-testid="fld-occupation" className="premium-input rounded" value={data.occupation} onChange={(e) => set("occupation", e.target.value)} placeholder="e.g. Software engineer, coach, student…" />
              </F>
              <F label="Daily activity level">
                <PillRadio testid="fld-daily-activity" value={data.daily_activity} onChange={(v) => set("daily_activity", v)} options={["Sedentary", "Active", "Very Active"]} />
              </F>
              <F label="Average hours of sleep">
                <Input data-testid="fld-sleep-hours" inputMode="numeric" className="premium-input rounded" value={data.sleep_hours} onChange={(e) => set("sleep_hours", e.target.value)} placeholder="7" />
              </F>
              <F label="Stress level">
                <PillRadio testid="fld-stress-level" value={data.stress_level} onChange={(v) => set("stress_level", v)} options={["Low", "Moderate", "High"]} />
              </F>
            </StepBox>
          )}

          {step === 5 && (
            <StepBox testid="step-health">
              <F label="Current medications">
                <Textarea data-testid="fld-medications" className="premium-textarea rounded" value={data.current_medications} onChange={(e) => set("current_medications", e.target.value)} placeholder="List anything currently prescribed" />
              </F>
              <F label="Current supplements">
                <Textarea data-testid="fld-supplements" className="premium-textarea rounded" value={data.current_supplements} onChange={(e) => set("current_supplements", e.target.value)} placeholder="Whey, creatine, vitamins…" />
              </F>
              <F label="Previous injuries or surgeries">
                <Textarea data-testid="fld-previous-injuries" className="premium-textarea rounded" value={data.previous_injuries} onChange={(e) => set("previous_injuries", e.target.value)} placeholder="Include date if relevant" />
              </F>
              <F label="Any medical conditions?">
                <Textarea data-testid="fld-medical-conditions" className="premium-textarea rounded" value={data.medical_conditions} onChange={(e) => set("medical_conditions", e.target.value)} placeholder="Diabetes, hypertension, thyroid, etc." />
              </F>
            </StepBox>
          )}

          {step === 6 && (
            <StepBox testid="step-progress">
              <p className="text-white/60 text-sm mb-4 leading-relaxed">
                Upload current progress photos in a well-lit space, minimal
                clothing / posing trunks. JPEG or PNG. We compress client-side.
              </p>
              <div className="grid sm:grid-cols-3 gap-4">
                <PhotoUpload testid="fld-front-photo" label="Front Photo" value={data.front_photo} onFile={(f) => handlePhoto("front_photo", f)} onClear={() => set("front_photo", "")} />
                <PhotoUpload testid="fld-side-photo" label="Side Photo" value={data.side_photo} onFile={(f) => handlePhoto("side_photo", f)} onClear={() => set("side_photo", "")} />
                <PhotoUpload testid="fld-back-photo" label="Back Photo" value={data.back_photo} onFile={(f) => handlePhoto("back_photo", f)} onClear={() => set("back_photo", "")} />
              </div>
              <div className="pt-6 border-t border-white/10 mt-6">
                <p className="text-[11px] text-white/60 tracking-[0.2em] uppercase mb-3 font-semibold">
                  Posing Video · Optional
                </p>
                <p className="text-white/50 text-sm mb-4">
                  Videos are usually too large to upload in a form. Send your
                  posing video directly to us on WhatsApp — Chris will review it
                  with your application.
                </p>
                <a
                  href={buildWhatsAppLink("Hi! I just submitted my Coaching Application. Sending my posing video now.")}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 bg-[#25D366] hover:bg-[#1FB855] text-white px-5 py-3 text-[11px] tracking-[0.25em] uppercase font-bold transition-colors"
                  data-testid="btn-video-whatsapp"
                >
                  <WhatsAppIcon size={16} />
                  Send Video via WhatsApp
                </a>
              </div>
            </StepBox>
          )}

          {step === 7 && (
            <StepBox testid="step-final">
              <F label="What is your biggest goal over the next 6–12 months?">
                <Textarea data-testid="fld-biggest-goal" className="premium-textarea min-h-[110px] rounded" value={data.biggest_goal} onChange={(e) => set("biggest_goal", e.target.value)} placeholder="Be specific — competition, physique, weight, placement…" />
              </F>
              <F label="What do you believe is currently holding you back?">
                <Textarea data-testid="fld-holding-back" className="premium-textarea min-h-[110px] rounded" value={data.whats_holding_back} onChange={(e) => set("whats_holding_back", e.target.value)} placeholder="Diet, training, mindset, recovery, coaching structure…" />
              </F>
            </StepBox>
          )}

          {step === 8 && (
            <StepBox testid="step-package">
              <F label="Which service are you interested in? *">
                <PillRadio
                  testid="fld-interested-service"
                  value={data.interested_service}
                  onChange={(v) => set("interested_service", v)}
                  cols={1}
                  options={[
                    "Nutrition Coaching",
                    "Training Coaching",
                    "Nutrition + Training",
                    "Contest Prep Coaching",
                    "Olympia Posing & Presentation",
                    "Complete VIP Coaching",
                  ]}
                />
              </F>
              <F label="Preferred method of communication *">
                <PillRadio
                  testid="fld-preferred-contact"
                  value={data.preferred_contact}
                  onChange={(v) => set("preferred_contact", v)}
                  options={["WhatsApp", "Email"]}
                />
              </F>
            </StepBox>
          )}

          {/* Nav */}
          <div className="flex flex-col sm:flex-row gap-3 pt-8 mt-2 border-t border-white/10">
            {step > 0 && (
              <Button
                type="button"
                onClick={back}
                variant="outline"
                className="border-white/20 text-white hover:bg-white/5 rounded-none py-6 text-[11px] tracking-[0.25em] uppercase font-bold"
                data-testid="btn-back"
              >
                <ArrowLeft size={14} className="mr-2" />
                Back
              </Button>
            )}
            {step < STEPS.length - 1 ? (
              <Button
                type="button"
                onClick={next}
                disabled={!stepValid()}
                className="flex-1 bg-[#D4AF37] text-black hover:bg-white disabled:opacity-50 disabled:hover:bg-[#D4AF37] rounded-none py-6 text-[11px] tracking-[0.25em] uppercase font-bold"
                data-testid="btn-next"
              >
                Continue
                <ArrowRight size={14} className="ml-2" />
              </Button>
            ) : (
              <Button
                type="button"
                onClick={handleSubmit}
                disabled={submitting || !stepValid()}
                className="flex-1 bg-[#D4AF37] text-black hover:bg-white disabled:opacity-50 disabled:hover:bg-[#D4AF37] rounded-none py-6 text-[11px] tracking-[0.25em] uppercase font-bold"
                data-testid="btn-submit"
              >
                {submitting ? (
                  <>
                    <Loader2 size={14} className="mr-2 animate-spin" />
                    SUBMITTING
                  </>
                ) : (
                  <>
                    Submit Application
                    <ArrowRight size={14} className="ml-2" />
                  </>
                )}
              </Button>
            )}
          </div>
        </div>

        <p className="text-center text-white/40 text-[11px] mt-6 tracking-wider">
          Prefer WhatsApp? Message +1 (619) 387-6711 — we&rsquo;ll walk you through it.
        </p>
      </div>
    </div>
  );
};

// -------------- Sub-components --------------
const StepHeading = ({ index, total, label }) => (
  <div className="mb-6">
    <p className="text-[#D4AF37] text-[10px] tracking-[0.35em] uppercase mb-1 font-semibold">
      Step {index} of {total}
    </p>
    <h2
      className="font-display text-2xl sm:text-3xl text-white leading-tight"
      style={{ fontFamily: "'Bebas Neue', sans-serif" }}
    >
      {label} Information
    </h2>
  </div>
);

const StepBox = ({ testid, children }) => (
  <div className="space-y-5" data-testid={testid}>
    {children}
  </div>
);

const F = ({ label, hint, children }) => (
  <div>
    <Label className="text-[11px] text-white/60 tracking-[0.2em] uppercase mb-2 block font-semibold">
      {label}
    </Label>
    {children}
    {hint && <p className="text-white/35 text-[11px] mt-1.5 italic">{hint}</p>}
  </div>
);

const TwoCol = ({ children }) => (
  <div className="grid sm:grid-cols-2 gap-4">{children}</div>
);

const PillRadio = ({ options, value, onChange, cols = 1, testid }) => {
  const gridClass =
    cols === 3 ? "sm:grid-cols-3" : cols === 2 ? "sm:grid-cols-2" : "sm:grid-cols-1";
  return (
    <RadioGroup value={value} onValueChange={onChange} className={`grid grid-cols-1 gap-2 ${gridClass}`} data-testid={testid}>
      {options.map((opt) => {
        const on = value === opt;
        const slug = opt.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/(^-|-$)/g, "");
        return (
          <label
            key={opt}
            className={`flex items-center justify-center py-3 px-3 border cursor-pointer transition-all text-sm text-center leading-tight ${
              on
                ? "border-[#D4AF37] bg-[#D4AF37]/[0.08] text-[#D4AF37]"
                : "border-white/15 text-white/70 hover:border-white/40"
            }`}
            data-testid={`${testid}-${slug}`}
          >
            <RadioGroupItem value={opt} className="sr-only" />
            <span className="font-medium">{opt}</span>
          </label>
        );
      })}
    </RadioGroup>
  );
};

const PhotoUpload = ({ label, value, onFile, onClear, testid }) => (
  <div className="relative border-2 border-dashed border-white/15 hover:border-[#D4AF37]/50 transition-colors aspect-[3/4] bg-black/40 flex items-center justify-center overflow-hidden" data-testid={testid}>
    {value ? (
      <>
        <img src={value} alt={label} className="w-full h-full object-cover" />
        <button
          type="button"
          onClick={onClear}
          className="absolute top-2 right-2 bg-black/80 hover:bg-red-600 text-white w-7 h-7 flex items-center justify-center border border-white/20 transition-colors"
          data-testid={`${testid}-clear`}
        >
          <X size={14} />
        </button>
        <span className="absolute bottom-2 left-2 bg-[#D4AF37] text-black text-[10px] font-bold px-2 py-1 tracking-wider">
          {label}
        </span>
      </>
    ) : (
      <label className="cursor-pointer text-center px-4 py-6 w-full h-full flex flex-col items-center justify-center">
        <Upload size={20} className="text-[#D4AF37] mb-2" />
        <span className="text-white text-sm font-semibold mb-1">{label}</span>
        <span className="text-white/40 text-[10px] tracking-wider">Tap to upload</span>
        <input
          type="file"
          accept="image/*"
          className="hidden"
          onChange={(e) => onFile(e.target.files?.[0])}
          data-testid={`${testid}-input`}
        />
      </label>
    )}
  </div>
);

export default CoachingApplicationPage;
